import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { getRequest } from 'api/apiClient';
import axios from 'axios';
import { DevicesByWorkflowType, IntegrationDataType, VideoDeviceType } from 'types/videoDeviceTypes';

interface AltaVideoState {
    videoDevices: VideoDeviceType[];
    devicesByWorkflow: Record<number, DevicesByWorkflowType>;
    altaVideoIntegrationData: IntegrationDataType[];
    isAltaVideoInstalled: boolean;
    isLoggedIn: boolean;
    thumbnailPresence: Record<string, Record<string, { SIGNIN?: boolean; SIGNOUT?: boolean }>>;
    thumbnailLoading: Record<string, Record<string, { SIGNIN?: boolean; SIGNOUT?: boolean }>>;
}

const initialState: AltaVideoState = {
    videoDevices: [],
    devicesByWorkflow: {},
    altaVideoIntegrationData: [],
    isAltaVideoInstalled: false,
    isLoggedIn: false,
    thumbnailPresence: {},
    thumbnailLoading: {},
};

const IS_LOCAL = window.location.hostname === 'localhost';
const LOCAL_PROXY = 'http://localhost:4000';

const getBaseUrl = (state: RootState) => {
    if (IS_LOCAL) return LOCAL_PROXY;
    const host = state.altaVideo.altaVideoIntegrationData?.[0]?.msiAltaVideoApiHost;
    return host ? `https://${host}` : '';
};

export const fetchAltaVideoIntegrationData = createAsyncThunk<{ integrationData: IntegrationDataType[]; isInstalled: boolean }, number>(
    'altaVideo/fetchAltaVideoIntegrationData',
    async (orgId) => {
        const res = await getRequest(`/orgs/${orgId}/integrationMsiAltaVideo`);
        const isInstalled = Array.isArray(res.data) && res.data.length > 0;
        return { integrationData: res.data, isInstalled };
    });

export const fetchDevicesByWorkflow = createAsyncThunk<DevicesByWorkflowType, { orgId: number; siteId: number }>(
    'altaVideo/fetchDevicesByWorkflow',
    async ({ orgId, siteId }) => {
        const res = await getRequest(`/orgs/${orgId}/sites/${siteId}/msiAltaVideoDevice`);
        return res.data;
    });

export const fetchVideoDevices = createAsyncThunk<VideoDeviceType[], number>(
    'altaVideo/fetchVideoDevices',
    async (orgId) => {
        const res = await getRequest(`/orgs/${orgId}/integrationMsiAltaVideo/videoDevices`);
        return res.data;
    }
);

export const botLoginAltaVideo = createAsyncThunk<void, void, { state: RootState }>(
    'altaVideo/botLoginAltaVideo',
    async (_: void, { getState, rejectWithValue }) => {

        if (getState().altaVideo.isLoggedIn) return;

        try {
            const BASE = getBaseUrl(getState());
            const creds = getState().altaVideo.altaVideoIntegrationData?.[0];
            if (!BASE || !creds?.msiAltaVideoUsername || !creds?.msiAltaVideoPassword) {
                return rejectWithValue('Missing Alta Video crendentials');
            }

            await axios.post(`${BASE}/api/v1/dologin`, {
                username: creds.msiAltaVideoUsername,
                password: creds.msiAltaVideoPassword,
            }, { withCredentials: true });
        } catch (error) {
            return rejectWithValue('Login failed');
        }
    }
)

export const botLogoutAltaVideo = createAsyncThunk<void, void, { state: RootState }>(
    'altaVideo/botLogoutAltaVideo',
    async (_: void, { getState, rejectWithValue }) => {
        try {
            const BASE = getBaseUrl(getState());

            await axios.post(`${BASE}/api/v1/logout`, {}, { withCredentials: true });

        } catch (error) {
            return rejectWithValue('Logout failed');
        }
    }
)

export const checkThumbnailPresence = createAsyncThunk<
    { visitId: string; visitorId: string; type: 'SIGNIN' | 'SIGNOUT'; has: boolean; deviceId: string },
    { visitId: string; visitorId: string; type: 'SIGNIN' | 'SIGNOUT'; time: number; deviceId: string },
    { state: RootState }
>('altaVideo/checkThumbnailPresence',
    async ({ visitId, visitorId, type, time, deviceId }, { getState, rejectWithValue }) => {
        try {
            const BASE = getBaseUrl(getState());
            const creds = getState().altaVideo.altaVideoIntegrationData?.[0];

            if (!BASE || !creds?.msiAltaVideoUsername || !creds?.msiAltaVideoPassword) {
                return rejectWithValue('Missing Alta Video crendentials');
            }

            const device = getState().altaVideo.videoDevices.find(d => d.guId === deviceId);
            if (!device || !device.sourceId?.length || device.errors.length) {
                return { visitId, visitorId, type, has: false, deviceId };
            }

            const url = `${BASE}/api/v1/devices/${device.guId}/sources/${device.sourceId[0]}/thumbnail`;

            try {
                const response = await axios.get(url, {
                    params: { time },
                    withCredentials: true,
                    responseType: 'blob',
                });

                if (response.data.size > 0) {
                    return { visitId, visitorId, type, has: true, deviceId };
                }
            } catch {

            }

            return { visitId, visitorId, type, has: false, deviceId };
        } catch {
            return rejectWithValue({ visitId, visitorId, type, deviceId });
        }
    }
);

const altaVideoSlice = createSlice({
    name: 'altaVideo',
    initialState,
    reducers: {
        resetAltaVideoForOrg(state) {
            state.videoDevices = [];
            state.devicesByWorkflow = {};
            state.altaVideoIntegrationData = [];
            state.isAltaVideoInstalled = false;
            state.isLoggedIn = false;
        },
        clearDevicesByWorkflow(state) {
            state.devicesByWorkflow = {};
        },
        resetThumbnailPresense(state) {
            state.thumbnailPresence = {};
        }

    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAltaVideoIntegrationData.fulfilled, (state, action) => {
                state.altaVideoIntegrationData = action.payload.integrationData;
                state.isAltaVideoInstalled = action.payload.isInstalled;

                if (!action.payload.isInstalled) {
                    state.videoDevices = [];
                    state.devicesByWorkflow = {};
                    state.isLoggedIn = false;
                }
            })
            .addCase(fetchAltaVideoIntegrationData.rejected, (state) => {
                state.isAltaVideoInstalled = false;
                state.videoDevices = [];
                state.devicesByWorkflow = {};
                state.altaVideoIntegrationData = [];
                state.isLoggedIn = false;
            })
            .addCase(fetchDevicesByWorkflow.fulfilled, (state, action: PayloadAction<DevicesByWorkflowType>) => {
                const siteId = action.payload.site.id;
                state.devicesByWorkflow[siteId] = action.payload;
            })
            .addCase(fetchVideoDevices.fulfilled, (state, action: PayloadAction<VideoDeviceType[]>) => {
                state.videoDevices = action.payload;
            })
            .addCase(botLoginAltaVideo.fulfilled, (state) => {
                state.isLoggedIn = true;
            })
            .addCase(botLoginAltaVideo.rejected, (state) => {
                state.isLoggedIn = false;
            })
            .addCase(botLogoutAltaVideo.fulfilled, (state) => {
                state.isLoggedIn = false;
            })
            .addCase(botLogoutAltaVideo.rejected, (state) => {
                state.isLoggedIn = true;
            })
            .addCase(checkThumbnailPresence.pending, (state, action) => {
                const { visitId, visitorId, type } = action.meta.arg;

                if (!state.thumbnailLoading[visitId]) {
                    state.thumbnailLoading[visitId] = {};
                }
                if (!state.thumbnailLoading[visitId][visitorId]) {
                    state.thumbnailLoading[visitId][visitorId] = {};
                }
                state.thumbnailLoading[visitId][visitorId][type] = true;
            })
            .addCase(checkThumbnailPresence.fulfilled, (state, action) => {
                const { visitId, visitorId, type, has } = action.payload;

                if (state.thumbnailLoading[visitId]?.[visitorId]) {
                    state.thumbnailLoading[visitId][visitorId][type] = false;
                }

                if (!state.thumbnailPresence[visitId]) {
                    state.thumbnailPresence[visitId] = {};
                }
                if (!state.thumbnailPresence[visitId][visitorId]) {
                    state.thumbnailPresence[visitId][visitorId] = {};
                }

                state.thumbnailPresence[visitId][visitorId][type] = has;
            })
            .addCase(checkThumbnailPresence.rejected, (state, action) => {
                const { visitId, visitorId, type } = action.meta.arg;

                if (state.thumbnailLoading[visitId]?.[visitorId]) {
                    state.thumbnailLoading[visitId][visitorId][type] = false;
                }

                if (!state.thumbnailPresence[visitId]) {
                    state.thumbnailPresence[visitId] = {};
                }
                if (!state.thumbnailPresence[visitId][visitorId]) {
                    state.thumbnailPresence[visitId][visitorId] = {};
                }
                state.thumbnailPresence[visitId][visitorId][type] = false;
            });
    },
});

export const { resetAltaVideoForOrg, clearDevicesByWorkflow, resetThumbnailPresense } = altaVideoSlice.actions;
export default altaVideoSlice.reducer;